import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type Channels, defaultOfficeOptions, type Offices } from 'constants/enums'
import { toast } from 'react-toastify'
import { getCommercialStructureData } from 'services/shared'
import type {
  BMU,
  CommercialStructureApi,
  Office,
  Regional,
  Zone,
} from 'services/shared/commercialStructure/interfaces'
import { getActiveQuotaMonthsByOffice, getYearMonthQuota } from 'services/shared/quotaMonth'
import type { QuotaMonthType } from 'services/shared/quotaMonth/interfaces'
import type { AppThunk } from 'store/store'

type InitialStateType = {
  loadingCommercialStructure: boolean
  hasCommercialStructure: boolean
  loadingQuotas: boolean
  commercialStructure: CommercialStructureApi[]
  bmus: BMU[]
  offices: Office[]
  regionals: Regional[]
  zones: Zone[]
  quotas: QuotaMonthType[]
}

const initialState: InitialStateType = {
  bmus: [] as BMU[],
  commercialStructure: [] as CommercialStructureApi[],
  hasCommercialStructure: false,
  loadingCommercialStructure: true,
  loadingQuotas: false,
  offices: [] as Office[],
  quotas: [] as QuotaMonthType[],
  regionals: [] as Regional[],
  zones: [] as Zone[],
}

const shared = createSlice({
  initialState,
  name: 'shared',
  reducers: {
    setBMUs(state, action: PayloadAction<BMU[]>) {
      state.bmus = action.payload
    },
    setCommercialStructure(state, action: PayloadAction<CommercialStructureApi[]>) {
      state.commercialStructure = action.payload
      state.hasCommercialStructure = action.payload.length > 0
    },
    setLoadingCommercialStructure(state, action: PayloadAction<boolean>) {
      state.loadingCommercialStructure = action.payload
    },
    setLoadingQuotas(state, action: PayloadAction<boolean>) {
      state.loadingQuotas = action.payload
    },
    setOffices(state, action: PayloadAction<Office[]>) {
      state.offices = action.payload
    },
    setQuotas(state, action: PayloadAction<QuotaMonthType[]>) {
      state.quotas = action.payload
    },
    setRegionals(state, action: PayloadAction<Regional[]>) {
      state.regionals = action.payload
    },
    setZones(state, action: PayloadAction<Zone[]>) {
      state.zones = action.payload
    },
  },
})

export const {
  setLoadingCommercialStructure,
  setCommercialStructure,
  setBMUs,
  setOffices,
  setRegionals,
  setZones,
  setLoadingQuotas,
  setQuotas,
} = shared.actions

export const sharedContext = shared.reducer

export const getCommercialStructure = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingCommercialStructure(true))

  try {
    const commercialStructure = await getCommercialStructureData()

    dispatch(setCommercialStructure(commercialStructure))
    dispatch(setCommercialStructureBMUs())
    dispatch(setCommercialStructureOffices())
    dispatch(setCommercialStructureRegionals())
    dispatch(setCommercialStructureZones())
  } catch (error) {
    toast.error('Erro ao carregar a Estrutura comercial.')
  } finally {
    dispatch(setLoadingCommercialStructure(false))
  }
}

export const getQuotas = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingQuotas(true))

  try {
    const quotas = await getYearMonthQuota()

    dispatch(setQuotas(quotas))
  } catch (error) {
    toast.error('Erro ao carregar quotas.')
  } finally {
    dispatch(setLoadingQuotas(false))
  }
}

export const getQuotasByOffice =
  (office: Offices): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingQuotas(true))

    try {
      const quotas = await getActiveQuotaMonthsByOffice(office)

      dispatch(setQuotas(quotas))
    } catch (error) {
      toast.error('Erro ao carregar quotas.')
    } finally {
      dispatch(setLoadingQuotas(false))
    }
  }

const setCommercialStructureBMUs = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure },
  } = getState()

  const bmus = commercialStructure.map((office) => ({
    bmuCode: office.CodigoUnidadeNegocio,
    bmuName: office.DescricaoUnidadeNegocio,
  })) as BMU[]

  const uniqueBMUs = Array.from(new Map(bmus.map((bmu) => [bmu.bmuCode, bmu])).values())

  dispatch(setBMUs(uniqueBMUs))
}

const setCommercialStructureOffices = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure },
  } = getState()

  const offices = commercialStructure.map((office) => ({
    description: office.DescricaoEscritorio,
    directorName: office.Diretor,
    id: office.CodigoEscritorio,
    idMarket: defaultOfficeOptions.find(
      (officeOption) => officeOption.label === office.CodigoEscritorio,
    )!.market as Channels,
  })) as Office[]

  dispatch(setOffices(offices))
}

const setCommercialStructureRegionals = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure },
  } = getState()

  const regionals = commercialStructure
    .flatMap((office) => office.Equipes) // NOTE: Regionais = Equipes
    .map((regional) => ({
      description: regional.Descricao,
      id: regional.Codigo,
      idOffice: commercialStructure.find((office) => office.Equipes.includes(regional))!
        .CodigoEscritorio as Offices,
    }))

  dispatch(setRegionals(regionals))
}

const setCommercialStructureZones = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure },
  } = getState()

  const zones = commercialStructure
    .flatMap((office) => office.Equipes) // NOTE: Regionais = Equipes
    .flatMap((regional) => regional.Regioes) // NOTE: Zonas = Regioes
    .map((zone) => ({
      description: zone.Descricao,
      id: zone.Codigo,
      idRegional: commercialStructure
        .flatMap((office) => office.Equipes)
        .find((regional) => regional.Regioes.includes(zone))!.Codigo,
    }))

  const uniqueZones = removeDuplicatedZones(zones)

  dispatch(setZones(uniqueZones))
}

const removeDuplicatedZones = (
  items: { id: string; description: string; idRegional: string }[],
) => {
  const uniqueZones = items.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.id === item.id &&
          t.description === item.description &&
          t.idRegional === item.idRegional,
      ),
  )

  return uniqueZones
}
